import type { Ref } from 'vue';

import { computed, isRef, onScopeDispose, ref } from 'vue';

interface AdaptivePaddingConfig {
	expandedPadding: number;
	collapsedPadding: number;
	dragBottomOffset: number;
	fallbackViewportHeight: number;
}

interface AdaptivePaddingInput {
	currentY: Ref<number> | number;
	isDragging: Ref<boolean> | boolean;
	forceExpanded?: Ref<boolean> | boolean;
	config?: Ref<Partial<AdaptivePaddingConfig>> | Partial<AdaptivePaddingConfig>;
}

interface AdaptivePaddingReturn {
	paddingValue: Ref<number>;
	transitionClasses: Ref<string>;
	dynamicStyle: Ref<{ paddingTop: string; paddingBottom: string }>;
}

const DEFAULT_CONFIG: AdaptivePaddingConfig = {
	expandedPadding: 96,
	collapsedPadding: 24,
	dragBottomOffset: 220,
	fallbackViewportHeight: 800
};

function calculateMaxDragDistance(
	config: AdaptivePaddingConfig,
	viewportHeight: number
): number {
	return viewportHeight - config.dragBottomOffset;
}

function normalizeProgress(currentY: number, maxDistance: number): number {
	return Math.min(Math.max(currentY / maxDistance, 0), 1);
}

function applyEaseInOutCubic(progress: number): number {
	return progress < 0.5
		? 2 * progress * progress
		: 1 - Math.pow(-2 * progress + 2, 3) / 2;
}

function interpolateBetweenValues(
	startValue: number,
	endValue: number,
	progress: number
): number {
	const easedProgress = applyEaseInOutCubic(progress);
	const difference = startValue - endValue;
	return startValue - difference * easedProgress;
}

function calculateAdaptivePadding(
	currentY: number,
	config: AdaptivePaddingConfig,
	forceExpanded: boolean,
	viewportHeight: number
): number {
	if (forceExpanded) {
		return config.expandedPadding;
	}

	const maxDragDistance = calculateMaxDragDistance(config, viewportHeight);
	const dragProgress = normalizeProgress(currentY, maxDragDistance);
	const interpolatedPadding = interpolateBetweenValues(
		config.expandedPadding,
		config.collapsedPadding,
		dragProgress
	);

	return Math.max(interpolatedPadding, config.collapsedPadding);
}

function generateTransitionClasses(isDragging: boolean): string {
	const baseClasses = '';
	const transitionClasses = isDragging
		? 'transition-none'
		: 'transition-all duration-500 ease-out';

	return `${baseClasses} ${transitionClasses}`.trim();
}

export function useAdaptivePadding(
	input: AdaptivePaddingInput
): AdaptivePaddingReturn {
	const configRef = computed(() => {
		const inputConfig = isRef(input.config) ? input.config.value : input.config;
		return { ...DEFAULT_CONFIG, ...inputConfig };
	});

	const currentYRef = computed(() =>
		typeof input.currentY === 'number' ? input.currentY : input.currentY.value
	);

	const isDraggingRef = computed(() =>
		typeof input.isDragging === 'boolean'
			? input.isDragging
			: input.isDragging.value
	);

	const forceExpandedRef = computed(() =>
		typeof input.forceExpanded === 'boolean'
			? input.forceExpanded
			: input.forceExpanded?.value || false
	);

	// Altura do viewport cacheada, atualizada só no `resize`. Ler
	// `window.innerHeight` a cada frame (como antes) força reflow síncrono
	// durante o scroll — `innerHeight` sincroniza o layout se ele estiver sujo,
	// e `paddingValue` recomputa a cada mudança de `currentY`.
	const viewportHeight = ref(
		import.meta.client
			? window.innerHeight
			: configRef.value.fallbackViewportHeight
	);

	if (import.meta.client) {
		const updateViewportHeight = (): void => {
			viewportHeight.value = window.innerHeight;
		};
		window.addEventListener('resize', updateViewportHeight, { passive: true });
		onScopeDispose(() =>
			window.removeEventListener('resize', updateViewportHeight)
		);
	}

	const paddingValue = computed(() =>
		calculateAdaptivePadding(
			currentYRef.value,
			configRef.value,
			forceExpandedRef.value,
			viewportHeight.value
		)
	);

	const transitionClasses = computed(() =>
		generateTransitionClasses(isDraggingRef.value)
	);

	const dynamicStyle = computed(() => ({
		paddingTop: `${paddingValue.value}px`,
		paddingBottom: `${paddingValue.value}px`
	}));

	return {
		paddingValue,
		transitionClasses,
		dynamicStyle
	};
}

export const ADAPTIVE_PADDING_PRESETS = {
	EMPTY_STATE: {
		expandedPadding: 96,
		collapsedPadding: 24,
		dragBottomOffset: 220,
		fallbackViewportHeight: 800
	},

	MODAL_CONTENT: {
		expandedPadding: 64,
		collapsedPadding: 16,
		dragBottomOffset: 120,
		fallbackViewportHeight: 600
	},

	CARD_CONTENT: {
		expandedPadding: 48,
		collapsedPadding: 12,
		dragBottomOffset: 100,
		fallbackViewportHeight: 400
	}
} as const;

export type {
	AdaptivePaddingConfig,
	AdaptivePaddingInput,
	AdaptivePaddingReturn
};
